import React, { useState, useEffect } from "react";
import { Typography, IconButton, Stack, TextField, Button, Grid, Box, Divider } from "@mui/material";
import { Close } from "@mui/icons-material";
import { globalTheme } from "../../../styles";
import { Drawer } from "../../Drawer";
import { useGetSuburbOrPostalCode } from "../../../hooks/useGetSuburbOrPostalCode";

interface CustomerEditDrawerProps {
  open: boolean;
  onClose: () => void;
  customerData: any;
  OnUpdatedCustomerData: (formData: any) => void;
}
interface Field {
  label?: string;
  key?: string;
  multiline?: boolean;
  error?: any;
  helperText?: any;
}

interface FieldGroup {
  title?: string;
  fields?: Field[];
}

export const CustomerEditDrawer = ({ open, onClose, customerData, OnUpdatedCustomerData }: CustomerEditDrawerProps) => {
  const [formData, setFormData] = useState({
    shippingFirstName: customerData.shippingFirstName || "",
    shippingLastName: customerData.shippingLastName || "",
    shippingCompanyName: customerData.shippingCompanyName || "",
    shippingDaytimePhone: customerData.shippingDaytimePhone || "",
    buyerEmailAddress: customerData.buyerEmailAddress || "",
    shippingAddressLine1: customerData.shippingAddressLine1 || "",
    shippingAddressLine2: customerData.shippingAddressLine2 || "",
    shippingCity: customerData.shippingCity || "",
    shippingPostalCode: customerData.shippingPostalCode || "",
    shippingStateOrProvince: customerData.shippingStateOrProvince || "",
    shippingCountry: customerData.shippingCountry || "",
  });

  const [editedFields, setEditedFields] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
  const [isSuburbOrPostcodeMatch, setIsSuburbOrPostcodeMatch] = useState(true);
  const [postCodeOrSuburb, setPostCodeOrSuburb] = useState<string | number>(formData.shippingCity || formData.shippingPostalCode);
  const [enabledSuburbOrPostalCode, setEnabledSuburbOrPostalCode] = useState(false);
  const [emailError, setEmailError] = useState(false);

  const { SuburbOrPostalCodeResponse, refetchSuburbOrPostalCode } = useGetSuburbOrPostalCode({
    enabled: enabledSuburbOrPostalCode,
    suburbOrPostalCode: { suburbOrPostcode: postCodeOrSuburb },
  });

  useEffect(() => {
    if (enabledSuburbOrPostalCode) {
      setEnabledSuburbOrPostalCode(false);
    }
  }, [enabledSuburbOrPostalCode]);

  useEffect(() => {
    if (SuburbOrPostalCodeResponse?.result && formData.shippingCountry === "AU") {
      const suburbExists = SuburbOrPostalCodeResponse.result.some(
        (response) =>
          response.Town.toLowerCase() === formData.shippingCity.toLowerCase() && response.Postcode === formData.shippingPostalCode
      );
      setIsSuburbOrPostcodeMatch(suburbExists);
    } else if (formData.shippingCountry !== "AU") {
      setIsSuburbOrPostcodeMatch(true);
    }
  }, [SuburbOrPostalCodeResponse, formData.shippingCity, formData.shippingPostalCode, formData.shippingCountry]);

  useEffect(() => {
    if (open) {
      setPostCodeOrSuburb(customerData.shippingPostalCode);

      setFormData({
        shippingFirstName: customerData.shippingFirstName || "",
        shippingLastName: customerData.shippingLastName || "",
        shippingCompanyName: customerData.shippingCompanyName || "",
        shippingDaytimePhone: customerData.shippingDaytimePhone || "",
        buyerEmailAddress: customerData.buyerEmailAddress || "",
        shippingAddressLine1: customerData.shippingAddressLine1 || "",
        shippingAddressLine2: customerData.shippingAddressLine2 || "",
        shippingCity: customerData.shippingCity || "",
        shippingPostalCode: customerData.shippingPostalCode || "",
        shippingStateOrProvince: customerData.shippingStateOrProvince || "",
        shippingCountry: customerData.shippingCountry || "",
      });

      setEditedFields(new Set());
      setErrors({});
      setEnabledSuburbOrPostalCode(true);
    }
  }, [open, customerData]);

  useEffect(() => {
    if (
      (formData.shippingCountry === "AU" && editedFields.has("shippingCity")) ||
      (formData.shippingCountry === "AU" && editedFields.has("shippingPostalCode"))
    ) {
      const timeoutId = setTimeout(() => {
        refetchSuburbOrPostalCode();
        setEnabledSuburbOrPostalCode(true);
      }, 1000);

      // Clean up timeout if effect is rerun before 1 seconds
      return () => clearTimeout(timeoutId);
    }
  }, [formData.shippingPostalCode, formData.shippingCountry, formData.shippingCity]);

  const fieldGroups: FieldGroup[] = [
    {
      title: "Name",
      fields: [
        {
          label: "First Name",
          key: "shippingFirstName",
          error: formData.shippingFirstName.length > 40,
          helperText: errors.shippingFirstName ? "Maximum 40 characters allowed" : "",
        },
        {
          label: "Last Name",
          key: "shippingLastName",
          error: formData.shippingLastName.length > 40,
          helperText: errors.shippingLastName ? "Maximum 40 characters allowed" : "",
        },
        {
          label: "Company",
          key: "shippingCompanyName",
          error: formData.shippingCompanyName.length > 40,
          helperText: errors.shippingCompanyName ? "Maximum 40 characters allowed" : "",
        },
        { label: "Phone", key: "shippingDaytimePhone" },
      ],
    },
    {
      title: "Email",
      fields: [
        {
          label: "Email",
          key: "buyerEmailAddress",
          error: emailError,
          helperText: emailError ? "Invalid email format" : "",
        },
      ],
    },
    {
      title: "Address",
      fields: [
        {
          label: "Address Line 1",
          key: "shippingAddressLine1",
          multiline: true,
          error: formData.shippingAddressLine1.length > 40,
          helperText: errors.shippingAddressLine1 ? "Maximum 40 characters allowed" : "",
        },
        {
          label: "Address Line 2",
          key: "shippingAddressLine2",
          multiline: true,
          error: formData.shippingAddressLine2.length > 40,
          helperText: errors.shippingAddressLine2 ? "Maximum 40 characters allowed" : "",
        },
        { label: "State/Province", key: "shippingStateOrProvince" },
        { label: "Country", key: "shippingCountry" },
        {
          label: "Suburb",
          key: "shippingCity",
          error: Boolean(!isSuburbOrPostcodeMatch),
          helperText: !isSuburbOrPostcodeMatch ? "Suburb and postal code do not match" : "",
        },
        {
          label: "Postal Code",
          key: "shippingPostalCode",
          error: Boolean(!isSuburbOrPostcodeMatch),
          helperText: !isSuburbOrPostcodeMatch ? "Suburb and postal code do not match" : "",
        },
      ],
    },
  ];

  const handleChange = (key: string, value: string) => {
    setPostCodeOrSuburb(value);
    setFormData((prev) => ({ ...prev, [key]: value }));
    setEditedFields((prev) => new Set(prev).add(key));

    if (
      key === "shippingAddressLine1" ||
      key === "shippingAddressLine2" ||
      key === "shippingFirstName" ||
      key === "shippingLastName" ||
      key === "shippingCompanyName" ||
      key === "shippingPostalCode" ||
      key === "shippingCity"
    ) {
      setErrors((prev) => ({ ...prev, [key]: value.length > 40 }));
    }
  };

  useEffect(() => {
    const isValidEmail = formData.buyerEmailAddress.includes("@") && formData.buyerEmailAddress.includes(".");
    setEmailError(!isValidEmail);
  }, [formData.buyerEmailAddress]);

  const isSaveDisabled =
    errors.shippingAddressLine1 ||
    errors.shippingAddressLine2 ||
    errors.shippingFirstName ||
    errors.shippingLastName ||
    errors.shippingCompanyName ||
    emailError;

  const handleSubmit = () => {
    OnUpdatedCustomerData(formData);
    onClose();
  };

  const handleSuburbSelect = (selectedTown, State, Postcode) => {
    setFormData((prevData) => ({
      ...prevData,
      shippingCity: selectedTown,
      shippingPostalCode: Postcode,
      shippingStateOrProvince: State,
    }));
  };

  return (
    <Drawer open={open} onClose={onClose}>
      <Stack sx={{ height: "100%", display: "flex" }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" ml={2} mr={1} mt={1.1}>
          <Typography
            variant="subtitle2"
            sx={{
              backgroundColor: globalTheme.background,
              color: globalTheme.primaryColor,
              p: 1,
              borderRadius: 2,
              width: "fit-content",
            }}
          >
            Edit Customer Details:
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              backgroundColor: globalTheme.background,
              color: globalTheme.primaryColor,
              p: 1,
              borderRadius: 2,
              width: "fit-content",
            }}
          >
            Order ID: {customerData.orderIds}
          </Typography>
        </Stack>

        <Box sx={{ overflowY: "auto", flexGrow: 1, p: 2, scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
          {fieldGroups.map((group) => (
            <Box key={group.title} mb={1}>
              <Typography
                variant="subtitle2"
                sx={{
                  backgroundColor: globalTheme.background,
                  color: globalTheme.primaryColor,
                  p: 1,
                  borderRadius: 2,
                  mb: 2,
                  width: "fit-content",
                  fontSize: 12,
                }}
              >
                {group.title}:
              </Typography>

              <Grid container spacing={2}>
                {group.fields.map((field) => (
                  <Grid item xs={12} sm={4} key={field.key}>
                    <TextField
                      label={field.label}
                      variant="outlined"
                      error={field.error}
                      helperText={field.helperText}
                      value={formData[field.key] || ""}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      fullWidth
                      multiline={field.multiline}
                      InputProps={{
                        sx: {
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor:
                              errors[field.key] ||
                              (!isSuburbOrPostcodeMatch && (field.key === "shippingCity" || field.key === "shippingPostalCode"))
                                ? "red"
                                : editedFields.has(field.key)
                                ? "orange"
                                : "inherit",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor:
                              errors[field.key] ||
                              (!isSuburbOrPostcodeMatch && (field.key === "shippingCity" || field.key === "shippingPostalCode"))
                                ? "red"
                                : editedFields.has(field.key)
                                ? "orange"
                                : "inherit",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor:
                              errors[field.key] ||
                              (!isSuburbOrPostcodeMatch && (field.key === "shippingCity" || field.key === "shippingPostalCode"))
                                ? "red"
                                : editedFields.has(field.key)
                                ? "orange"
                                : "primary.main",
                          },
                        },
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}

          {formData && (
            <>
              {SuburbOrPostalCodeResponse && !isSuburbOrPostcodeMatch && (
                <Box
                  mt={2}
                  sx={{
                    border: "1px solid",
                    borderColor: globalTheme.border,
                    borderRadius: "8px",
                    padding: "10px",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "16px", fontWeight: "bold", paddingBottom: "10px", color: globalTheme.primaryColor }}
                  >
                    Address Search Results
                  </Typography>
                  <Stack spacing={1} sx={{ maxHeight: "150px", overflow: "scroll" }}>
                    {SuburbOrPostalCodeResponse?.result?.map((a, key) => (
                      <Stack
                        direction="row"
                        spacing={5}
                        padding={1}
                        key={key}
                        sx={{
                          fontSize: "12px",
                          fontWeight: "300",
                          backgroundColor: globalTheme.background,
                          borderRadius: "6px",
                          cursor: "pointer",
                        }}
                        alignItems="center"
                        onClick={() => handleSuburbSelect(a.Town, a.State, a.Postcode)}
                      >
                        <Typography sx={{ width: "150px" }}>{a.Town}</Typography>
                        <Typography sx={{ width: "50px" }}>{a.State}</Typography>
                        <Typography sx={{ width: "50px" }}>{a.Postcode}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}
            </>
          )}
        </Box>

        <Divider flexItem />
        {/* Buttons at the Bottom */}
        <Stack direction="row" spacing={2} justifyContent="flex-end" p={2}>
          <Button variant="outlined" color="error" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={isSaveDisabled}>
            Save
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};
